type LiveWorkspacesT = {
  eyebrow: string;
  title: React.ReactNode;
  body: string;
  panelHeader: string;
  panelActive: string;
  features: [string, string, string][];
};

const defaultT: LiveWorkspacesT = {
  eyebrow: "Section 02 — Live workspaces",
  title: <>Real-time <em className="urgent-cyan">collaborative</em> workspaces.</>,
  body: "Create or join online spaces designed for focus, productivity, collaboration, and shared learning.",
  panelHeader: "Live presence · 24h",
  panelActive: "2,481 active",
  features: [
    ["01", "Live rooms", "Drop into active rooms anytime."],
    ["02", "Pomodoro timers", "Synchronized focus and break cycles."],
    ["03", "Shared workspaces", "Co-work in persistent online spaces."],
    ["04", "Study streams", "Camera-on accountability sessions."],
    ["05", "Group sessions", "Collaborate in real time with cohorts."],
  ],
};

function PresenceChart() {
  const pts = [12, 14, 10, 8, 9, 16, 28, 42, 55, 62, 70, 74, 78, 82, 80, 76, 84, 92, 88, 70, 56, 40, 28, 18];
  const max = 100;
  const w = 600, h = 180;
  const step = w / (pts.length - 1);
  const line = pts.map((p, i) => `${i * step},${h - (p / max) * h}`).join(" ");
  const area = `0,${h} ${line} ${w},${h}`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-full text-primary" preserveAspectRatio="none" aria-hidden="true">
      <defs>
        <linearGradient id="presenceFill" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.35" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
        </linearGradient>
      </defs>
      {[0.25, 0.5, 0.75].map((r) => (
        <line key={r} x1="0" y1={h * r} x2={w} y2={h * r} stroke="currentColor" strokeWidth="0.4" opacity="0.15" />
      ))}
      <polygon points={area} fill="url(#presenceFill)" />
      <polyline points={line} fill="none" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

export const LiveWorkspaces = ({ t = defaultT }: { t?: LiveWorkspacesT }) => {
  return (
    <section className="py-24 lg:py-32 border-t border-border/40" aria-labelledby="live-workspaces-title">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <span className="eyebrow-neon">{t.eyebrow}</span>
            <h2 id="live-workspaces-title" className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-foreground">
              {t.title}
            </h2>
            <div className="hairline w-32 mt-8 mb-6" />
            <p className="text-lg text-muted-foreground leading-relaxed max-w-md">
              {t.body}
            </p>
          </div>

          <div className="lg:col-span-7">
            <div className="border border-border bg-card/40 backdrop-blur-sm">
              <div className="flex items-center justify-between px-5 py-3 border-b border-border eyebrow">
                <span>{t.panelHeader}</span>
                <span className="tabular-nums text-foreground">{t.panelActive}</span>
              </div>
              <div className="aspect-[16/5]">
                <PresenceChart />
              </div>
            </div>

            <ol className="mt-8 grid sm:grid-cols-2 gap-x-8 gap-y-6">
              {t.features.map(([n, title, body]) => (
                <li key={title} className="border-t border-border pt-4">
                  <div className="eyebrow mb-2">{n}</div>
                  <h3 className="font-display text-xl text-foreground mb-1">{title}</h3>
                  <p className="text-sm text-muted-foreground leading-snug">{body}</p>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </section>
  );
};
